import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import './EmojiGrid.css';
import EmojiDetailsModal from './EmojiDetailsModal';

// Utility function to format large numbers
const formatNumber = (num) => {
  // Handle edge cases
  if (typeof num !== 'number' || isNaN(num)) return '0';
  if (!isFinite(num)) return num > 0 ? '∞' : '-∞';
  if (num < 0) return '-' + formatNumber(-num);
  
  // Use absolute value for calculations
  const absNum = Math.abs(num);
  
  // Format with appropriate suffix
  if (absNum >= 1000000000000000) {
    // Quadrillions (Qa) - using Qa for clarity
    return (absNum / 1000000000000000).toFixed(1).replace(/\.0$/, '') + 'Qa';
  }
  if (absNum >= 1000000000000) {
    // Trillions (T)
    return (absNum / 1000000000000).toFixed(1).replace(/\.0$/, '') + 'T';
  }
  if (absNum >= 1000000000) {
    // Billions (B)
    return (absNum / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (absNum >= 1000000) {
    // Millions (M)
    return (absNum / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (absNum >= 1000) {
    // Thousands (K)
    return (absNum / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  // For numbers < 1000, use locale formatting
  return absNum.toLocaleString();
};

// Boring symbol emojis that clutter the grid — technically valid Unicode emoji
// but not what people think of as "emojis" (math operators, media controls,
// arrows, keycaps, geometric shapes, copyright/trademark, etc.)
const BORING_SYMBOLS = new Set([
  // Math operators
  '➕', '➖', '➗', '✖️', '✖',
  // Media controls
  '▶️', '▶', '◀️', '◀', '⏩', '⏪', '⏫', '⏬',
  '⏭️', '⏭', '⏮️', '⏮', '⏯️', '⏯', '⏸️', '⏸',
  '⏹️', '⏹', '⏺️', '⏺', '⏏️', '⏏',
  // Geometric shapes
  '⬛', '⬜', '◼️', '◼', '◻️', '◻', '◾', '◽',
  '▪️', '▪', '▫️', '▫', '🔲', '🔳', '🔘',
  // Arrows
  '⬆️', '⬆', '⬇️', '⬇', '⬅️', '⬅', '➡️', '➡',
  '↕️', '↕', '↔️', '↔', '↗️', '↗', '↘️', '↘',
  '↙️', '↙', '↖️', '↖', '↩️', '↩', '↪️', '↪',
  '🔼', '🔽', '🔀', '🔁', '🔂',
  // Keycaps
  '#️⃣', '*️⃣',
  '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣',
  '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟',
  // Copyright / trademark
  '©️', '©', '®️', '®', '™️', '™',
  // Misc symbols
  '〰️', '〰', '〽️', '〽', '⚕️', '⚕',
  '🔗',
]);

// Utility function to check if an emoji is valid (not a single regional indicator)
// Regional indicators (🇦-🇿) should only appear as pairs in flag emojis
const isValidEmoji = (emoji) => {
  if (!emoji || typeof emoji !== 'string') return false;
  
  // Filter boring symbol emojis
  if (BORING_SYMBOLS.has(emoji)) return false;
  
  // Check first codepoint for standalone modifiers / regional indicators
  const cp = emoji.codePointAt(0);
  if (cp !== undefined) {
    // Standalone skin tone modifiers (U+1F3FB–U+1F3FF) — not real emojis
    if (cp >= 0x1F3FB && cp <= 0x1F3FF) return false;
    // Single regional indicator (should only appear as flag pairs)
    if (emoji.length === 2 && cp >= 0x1F1E6 && cp <= 0x1F1FF) return false;
  }
  
  return true;
};

// Compute visual cues from spike multiplier: count color + emoji glow
// Returns { countColor, glowFilter } or {} when idle
const lerp = (a, b, t) => Math.round(a + (b - a) * t);

const getVelocityVisuals = (spike, trend) => {
  if (!spike || spike <= 1.1) return {};

  // t: 0 at 1× baseline → 1 at 10× baseline
  const t = Math.min((spike - 1) / 9, 1);

  // Count text: lerp from white → green (up) or amber (down)
  const target = trend === 'down' ? [251, 191, 36] : [34, 197, 94];
  const countColor = `rgb(${lerp(255, target[0], t)}, ${lerp(255, target[1], t)}, ${lerp(255, target[2], t)})`;

  // Emoji aura: soft drop-shadow, visible at 2×+ baseline
  let glowFilter;
  if (spike > 2) {
    const gt = Math.min((spike - 2) / 8, 1);
    const size = Math.round(6 + gt * 10);
    const alpha = (0.2 + gt * 0.3).toFixed(2);
    const gc = trend === 'down'
      ? `rgba(251, 191, 36, ${alpha})`
      : `rgba(34, 197, 94, ${alpha})`;
    glowFilter = `drop-shadow(0 0 ${size}px ${gc})`;
  }

  return { countColor, glowFilter };
};

// Create enhanced particle effect helper function
const createParticles = (event, emoji) => {
  if (!event || !event.currentTarget) {
    return;
  }
  
  const target = event.currentTarget;
  const rect = target.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  
  // Create more particles for better effect
  const particleCount = 12;
  const particles = ['✨', '⭐', '💫', '🌟', '🎉', '💖', '🔥', '🚀'];
  
  for (let i = 0; i < particleCount; i++) {
    const particle = document.createElement('div');
    particle.className = 'emoji-particle';
    // Sometimes use the clicked emoji itself
    particle.textContent = Math.random() > 0.7 ? emoji : particles[Math.floor(Math.random() * particles.length)];
    particle.style.left = centerX + 'px';
    particle.style.top = centerY + 'px';
    particle.style.position = 'fixed';
    particle.style.pointerEvents = 'none';
    particle.style.zIndex = '9999';
    particle.style.fontSize = (16 + Math.random() * 12) + 'px';
    
    const angle = (Math.PI * 2 * i) / particleCount;
    const distance = 60 + Math.random() * 40;
    const endX = Math.cos(angle) * distance;
    const endY = Math.sin(angle) * distance;
    
    particle.style.animation = `particleFloat ${0.6 + Math.random() * 0.6}s ease-out forwards`;
    particle.style.setProperty('--end-x', endX + 'px');
    particle.style.setProperty('--end-y', endY + 'px');
    particle.style.opacity = '0';
    
    document.body.appendChild(particle);
    
    setTimeout(() => {
      if (particle.parentNode) {
        particle.remove();
      }
    }, 1500);
  }
};

// TrendingEmojis component for showing velocity-based trending
export const TrendingEmojis = ({ emojiData = {}, trendingData = [], trendingSummary = '', onEmojiCopied, onAddToWatchlist = null, trackedEmojis = [], getServerTime = null, sessionStartTime = null }) => {
  const [selectedEmoji, setSelectedEmoji] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTrendingClick = (emoji, event) => {
    if (!event || !event.currentTarget) return;
    if (event.preventDefault) event.preventDefault();
    if (event.stopPropagation) event.stopPropagation();
    setSelectedEmoji(emoji);
    setIsModalOpen(true);
    document.body.classList.add('modal-open');
    createParticles(event);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmoji(null);
    document.body.classList.remove('modal-open');
  };

  const getEmojiData = () => {
    let merged = { ...emojiData };
    if (trendingData && trendingData.length > 0) {
      trendingData.forEach(item => {
        if (item.emoji && item.count !== undefined) {
          const existing = merged[item.emoji];
          const existingData = typeof existing === 'object' 
            ? existing 
            : (existing ? { count: existing } : { count: 0 });
          merged[item.emoji] = {
            ...existingData,
            count: item.count || existingData.count || 0,
            postCount: item.postCount || existingData.postCount || 0,
          };
        }
      });
    }
    return merged;
  };

  // Compute valid trending items
  const validItems = trendingData.filter(({ emoji }) => isValidEmoji(emoji)).slice(0, 14);

  // Heat tier: maps spike multiplier → CSS class for warm border + glow + pulse
  const getHeatClass = (spike, index) => {
    if (!spike || spike <= 1.2) return '';
    if (index === 0 && spike > 3) return 'heat-max';
    if (spike > 5) return 'heat-high';
    if (spike > 2) return 'heat-mid';
    return 'heat-low';
  };

  return (
    <div className="trending-emojis">
      <h2>
        🔥 Trending Now
        <span className="trending-info-tip" data-tip="Ranked by spike vs. 1-hour baseline. 5× means 5 times more active than its usual rate. Higher multipliers = bigger spikes.">ⓘ</span>
      </h2>
      {trendingSummary && (
        <p className="trending-summary">{trendingSummary}</p>
      )}
      <div className="trending-container">
        {validItems.length > 0 ? (
          validItems.map(({ emoji, count, velocity, spike, trend, postRate }, index) => {
            const v = getVelocityVisuals(spike, trend);
            const heatClass = getHeatClass(spike, index);
            // Format spike multiplier: "5.2×" or "12×" (drop decimal for ≥10)
            const fmtSpike = (s) => {
              if (!s || s < 0.1) return '—';
              if (s >= 10) return `${Math.round(s)}×`;
              return `${s.toFixed(1)}×`;
            };
            return (
            <div 
              key={emoji} 
              className={`trending-item ${heatClass}`}
              style={{ animationDelay: `${index * 0.05}s` }}
              onClick={(e) => handleTrendingClick(emoji, e)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTrendingClick(emoji, e); } }}
              tabIndex={0}
              role="button"
              aria-label={`${emoji} trending #${index + 1}, ${fmtSpike(spike)} usual rate`}
            >
              <div className="trending-emoji" style={v.glowFilter ? { filter: v.glowFilter } : undefined}>{emoji}</div>
              <div className="trending-info">
                <div className="trending-velocity" style={v.countColor ? { color: v.countColor } : undefined}>
                  {fmtSpike(spike)}
                </div>
              </div>
              <div className="trending-rank">#{index + 1}</div>
            </div>
            );})
        ) : (
          <>
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="trending-item skeleton" style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="skeleton-emoji" />
                <div className="skeleton-count" />
                <div className="skeleton-rank" />
              </div>
            ))}
          </>
        )}
      </div>

      <EmojiDetailsModal
        emoji={selectedEmoji}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        emojiData={getEmojiData()}
        trendingData={trendingData}
        onAddToWatchlist={onAddToWatchlist}
        trackedEmojis={trackedEmojis}
        getServerTime={getServerTime}
        sessionStartTime={sessionStartTime}
      />
    </div>
  );
};

const EmojiGrid = memo(({ emojiData, onEmojiCopied, trackedEmojis = [], onAddToWatchlist = null, trendingData = [], getServerTime = null, sessionStartTime = null }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [animatedCounts, setAnimatedCounts] = useState({});
  const [selectedEmoji, setSelectedEmoji] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const previousEmojisRef = useRef([]);

  // Helper to get count from emojiData
  const getEmojiCount = (data) => {
    if (typeof data === 'number') return data;
    return data?.count || 0;
  };

  // Convert emoji data to array and sort - memoized to prevent infinite loops
  const currentSortedEmojis = useMemo(() => {
    const emojis = Object.entries(emojiData)
      .filter(([emoji]) => {
        // Filter out invalid emojis and empty/whitespace-only strings
        return isValidEmoji(emoji) && emoji.trim().length > 0;
      })
      .map(([emoji, data]) => {
        const baseCount = getEmojiCount(data);
        
        // Prefer trendingData for count/postCount (more up-to-date for top 500 emojis)
        // Fall back to emojiData for emojis not in trendingData
        // Note: Watchlist emojis outside top 500 won't be in trendingData, but they're always
        // included in topEmojis from backend with fresh counts, so emojiData will have correct values
        const trendingMatch = trendingData.find(t => t.emoji === emoji);
        const count = trendingMatch?.count !== undefined ? trendingMatch.count : baseCount;
        
        // Preserve trend info from emojiData (from backend topEmojis) or trendingData
        const trend = (typeof data === 'object' && data?.trend !== undefined) 
          ? data.trend 
          : (trendingMatch?.trend || 'stable');
        const velocity = (typeof data === 'object' && data?.velocity !== undefined)
          ? data.velocity
          : (trendingMatch?.velocity || 0);
        
        return { 
          emoji, 
          count, 
          trend,
          velocity
        };
      });
    
    // Sort by count (always)
    const sorted = emojis.sort((a, b) => b.count - a.count);
    
    return sorted.slice(0, 100);
  }, [emojiData, trendingData]);

  // Handle loading state
  useEffect(() => {
    if (currentSortedEmojis.length > 0 && isLoading) {
      setIsLoading(false);
    }
  }, [currentSortedEmojis.length, isLoading]);

  // Track active animation frames for cleanup
  const activeAnimations = useRef(new Map());
  const previousCountsRef = useRef({});

  // Animate count changes with visual feedback
  // Optimized: Only animate if count changed significantly or is in top 20
  useEffect(() => {
    // Limit animations to top 20 emojis to reduce CPU usage
    const topEmojisToAnimate = currentSortedEmojis.slice(0, 20);
    
    topEmojisToAnimate.forEach(({ emoji, count }) => {
      const prevCount = previousCountsRef.current[emoji] || animatedCounts[emoji] || 0;
      
      // Only animate if count changed and difference is significant (>5% or >10)
      const difference = Math.abs(count - prevCount);
      const shouldAnimate = difference > Math.max(10, prevCount * 0.05);
      
      if (count !== prevCount && prevCount > 0 && shouldAnimate) {
        // Cancel any existing animation for this emoji
        const existingAnimation = activeAnimations.current.get(emoji);
        if (existingAnimation) {
          cancelAnimationFrame(existingAnimation.frameId);
        }

        // Animate the count change with bounce effect
        const duration = 400; // Reduced from 600ms for snappier feel
        const startTime = Date.now();
        const startValue = prevCount;
        const endValue = count;
        const diff = endValue - startValue;

        let frameId = null;

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // Simplified easing for better performance
          const easeOut = 1 - Math.pow(1 - progress, 3);
          const currentValue = Math.floor(startValue + diff * easeOut);

          setAnimatedCounts(prev => ({
            ...prev,
            [emoji]: currentValue
          }));

          if (progress < 1) {
            frameId = requestAnimationFrame(animate);
            activeAnimations.current.set(emoji, { frameId });
          } else {
            setAnimatedCounts(prev => ({
              ...prev,
              [emoji]: endValue
            }));
            activeAnimations.current.delete(emoji);
            previousCountsRef.current[emoji] = endValue;
          }
        };

        frameId = requestAnimationFrame(animate);
        activeAnimations.current.set(emoji, { frameId });
      } else if (prevCount === 0) {
        // Initial value - no animation needed
        setAnimatedCounts(prev => ({
          ...prev,
          [emoji]: count
        }));
        previousCountsRef.current[emoji] = count;
      } else {
        // Update ref even if not animating
        previousCountsRef.current[emoji] = count;
        // Update animated counts directly for non-top emojis
        if (!topEmojisToAnimate.find(e => e.emoji === emoji)) {
          setAnimatedCounts(prev => ({
            ...prev,
            [emoji]: count
          }));
        }
      }
    });

    // Cleanup function to cancel all animations when component unmounts or data changes
    const currentAnimations = activeAnimations.current;
    return () => {
      currentAnimations.forEach(({ frameId }) => {
        if (frameId !== null) cancelAnimationFrame(frameId);
      });
      currentAnimations.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSortedEmojis]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmoji(null);
    document.body.classList.remove('modal-open');
  };

  // Track previous emojis for ref
  useEffect(() => {
    const currentKey = currentSortedEmojis.map(e => `${e.emoji}:${e.count}`).join('|');
    const previousKey = previousEmojisRef.current.map(e => `${e.emoji}:${e.count}`).join('|');
    if (currentKey !== previousKey) {
      previousEmojisRef.current = currentSortedEmojis;
    }
  }, [currentSortedEmojis]);

  return (
    <div className="emoji-grid">
      <h2>🏆 Top Emojis</h2>
      <div className="skyline-container">
        {isLoading ? (
          <div className="grid-container">
            {Array.from({ length: 20 }, (_, i) => (
              <div key={i} className="emoji-item skeleton" style={{ animationDelay: `${i * 0.04}s` }}>
                <div className="skeleton-emoji" />
                <div className="skeleton-count" />
                <div className="skeleton-rank" />
              </div>
            ))}
          </div>
        ) : currentSortedEmojis.length > 0 ? (
          <div className="grid-container">
            {currentSortedEmojis.slice(0, 100).map((item, index) => {
              const emoji = item.emoji;
              const count = item.count || 0;
              const v = getVelocityVisuals(item.velocity, item.trend);
              
              return (
                <div
                  key={emoji}
                  className="emoji-item"
                  onClick={() => {
                    setSelectedEmoji(emoji);
                    setIsModalOpen(true);
                    document.body.classList.add('modal-open');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedEmoji(emoji);
                      setIsModalOpen(true);
                      document.body.classList.add('modal-open');
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`${emoji} ranked #${index + 1} with ${formatNumber(count)} uses`}
                >
                  <span className="emoji-char" style={v.glowFilter ? { filter: v.glowFilter } : undefined}>{emoji}</span>
                  <div className="emoji-count" style={v.countColor ? { color: v.countColor } : undefined}>{formatNumber(count)}</div>
                  <div className="emoji-rank">#{index + 1}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-data" style={{ textAlign: 'center', padding: '40px', color: 'rgba(255, 255, 255, 0.6)' }}>
            <p>No emoji data yet</p>
          </div>
        )}
      </div>

      <EmojiDetailsModal
        emoji={selectedEmoji}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        emojiData={emojiData}
        trendingData={trendingData}
        onAddToWatchlist={onAddToWatchlist}
        trackedEmojis={trackedEmojis}
        getServerTime={getServerTime}
        sessionStartTime={sessionStartTime}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if emojiData actually changed
  // Compare top emojis (most likely to change)
  const prevTop = Object.entries(prevProps.emojiData)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 50)
    .map(([e, c]) => `${e}:${c}`)
    .join('|');
  const nextTop = Object.entries(nextProps.emojiData)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 50)
    .map(([e, c]) => `${e}:${c}`)
    .join('|');
  
  const prevTracked = prevProps.trackedEmojis || [];
  const nextTracked = nextProps.trackedEmojis || [];
  
  return prevTop === nextTop && 
         prevTracked.length === nextTracked.length &&
         prevTracked.every((e, i) => e === nextTracked[i]);
});

EmojiGrid.displayName = 'EmojiGrid';

export default EmojiGrid;

